import { creatorbase } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import { NamedProps, ValueType } from 'react-select';
import useSelectProps from 'Hooks/useSelectProps';
import useInfiniteCampaignsData, { CampaignData } from './useInfiniteCampaignsData';
import useCampaignData from './useCampaignData';
import { isNumber } from 'utility/utility';
import { useMemo } from 'react';

export type CampaignOption = GenericDropdownOption<number> & CampaignData;

export const mapCampaignToOption = (
    campaign: creatorbase.Campaign,
    { project, brand, team }: { project?: creatorbase.Project; brand?: creatorbase.Brand; team?: creatorbase.Team } = {}
): CampaignOption => {
    return {
        ...campaign,
        value: campaign.id,
        label: campaign.name,
        project: project ?? null,
        brand: brand ?? null,
        team: team ?? null,
    };
};

type PropGetter = (
    override?: Pick<NamedProps<CampaignOption>, 'onChange' | 'isDisabled'>
) => NamedProps<CampaignOption>;

type Return = { getProps: PropGetter; value: ValueType<CampaignOption, false>; resetValue: () => void };

type Params = Partial<{
    initialValueId: number | null;
    params: Pick<
        creatorbase.GetCampaignsParams,
        'currency_id' | 'exclude_allocated_to_purchase_id' | 'exclude_fully_allocated' | 'client_id'
    >;
    enabled: boolean;
}>;

export default function useCampaignSelect({ initialValueId, params, enabled = true }: Params): Return {
    const { campaign, project, brand } = useCampaignData(initialValueId, {
        enabled: isNumber(initialValueId) && enabled,
    });
    const initialValue = useMemo(
        () => (campaign ? mapCampaignToOption(campaign, { project, brand }) : null),
        [brand, campaign, project]
    );

    const { getProps, resetValue, isMenuOpen, debouncedSearchValue } = useSelectProps<CampaignOption, false>({
        initialValue,
    });

    const { queries, isFetching, hasNextPage, fetchNextPage } = useInfiniteCampaignsData(
        { ...params, page_size: 25, search: debouncedSearchValue },
        { enabled: isMenuOpen }
    );

    const options: CampaignOption[] =
        queries.campaign.data?.pages.flatMap((page, pageIndex) => {
            const projectsResult = queries.projects[pageIndex];
            const brandsResult = queries.brands[pageIndex];
            const teamsResult = queries.teams[pageIndex];
            if (!projectsResult?.isSuccess || !brandsResult?.isSuccess || !teamsResult?.isSuccess) {
                return [];
            }

            return page.results.map((c) => {
                const project = projectsResult.data.results.find((p) => p.id === c.project_id);
                return mapCampaignToOption(c, {
                    project,
                    brand: brandsResult.data.results.find((b) => b.id === project?.brand_id),
                    team: teamsResult.data.results.find((t) => t.id === project?.team_id),
                });
            });
        }) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                noOptionsMessage: () => 'No campaigns found',
                isLoading:
                    isFetching ||
                    queries.brands.some((r) => r.isFetching) ||
                    queries.projects.some((r) => r.isFetching) ||
                    queries.teams.some((r) => r.isFetching),
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
            }),
        value: getProps().value,
        resetValue,
    };
}
